import React, { useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { initialValues, validationSchema } from './constants';

function EditQuestion({ isOpen, toggle, header, qaDetails, onSubmit }) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const quillRef = useRef(null);

  const toolbarOptions = [
    [{ font: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'], // toggled buttons
    [{ color: [] }, { background: [] }], // dropdown with defaults from theme
    [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
    [{ direction: 'rtl' }], // text direction
    ['blockquote', 'code-block'],
    ['link', 'video'],
    ['clean'], // remove formatting button
  ];
  useEffect(() => {
    if (isOpen && qaDetails) {
      reset({
        question: qaDetails.Question || '',
        answer: qaDetails.Answer || '',
      });

      // Set HTML content in the editor
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        const delta = editor.clipboard.convert(qaDetails.Answer || '');
        editor.setContents(delta, 'silent');
      }
    }
  }, [isOpen, qaDetails, reset]);

  const handleFormSubmit = formData => {
    const payload = {
      qaId: qaDetails?.['QA ID'],
      question: formData.question,
      answer: formData.answer,
    };
    onSubmit(payload);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl">
      <ModalHeader
        toggle={() => {
          toggle();
          reset();
        }}
      >
        Update Question & Answer Under {header?.Title}
      </ModalHeader>
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <ModalBody>
          <div className="d-flex flex-column gap-3">
            <div>
              <Label>
                Question<span className="text-danger">*</span>
              </Label>
              <Controller
                name="question"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Enter your question here"
                    type="text"
                    className={`form-control external-filter-input ${errors.question ? 'is-invalid' : ''}`}
                  />
                )}
              />
              {errors.question && (
                <div className="invalid-feedback">
                  {errors.question.message}
                </div>
              )}
            </div>
            <div>
              <Label>
                Answer<span className="text-danger">*</span>
              </Label>
              <Controller
                name="answer"
                control={control}
                render={({ field }) => (
                  <div
                    className={`rich-editor-wrapper ${errors.answer ? 'is-invalid' : ''}`}
                  >
                    <ReactQuill
                      {...field}
                      ref={quillRef}
                      theme="snow"
                      placeholder="Enter your answer here"
                      onChange={field.onChange}
                      value={field.value}
                      modules={{ toolbar: toolbarOptions }}
                    />
                  </div>
                )}
              />
              {errors.answer && (
                <div className="invalid-feedback">{errors.answer.message}</div>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-start">
          <button
            disabled={!isValid}
            type="submit"
            className="custom-secondary-btn"
          >
            Update
          </button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default EditQuestion;
